import { Accordion, AccordionDetails, Stack } from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { SearchSummary } from "store/api/generatedApi";
import { HistoryChildItem } from "./HistoryChildItem";
import { HistoryItemTitle } from "./HistoryItemTitle";
import { useDiffDisplayContext } from "components/search/input/DiffDisplayContext";

const checkIfBetweenActiveDiffSearches = (
  targetSearchId: number | undefined,
  sourceSearchId: number | undefined,
  searchId: number
) => {
  // can only be inbetween if both source and target are selected
  if (targetSearchId === undefined || sourceSearchId === undefined)
    return false;

  return searchId < targetSearchId && searchId > sourceSearchId;
};

interface Props {
  versionNumber: number;
  search: SearchSummary;
  searchChildren: SearchSummary[];
  currentSearchId: number;
  onSelect: (search: SearchSummary) => void;
  parentSearchId?: number;
}

export const HistoryItem = ({
  versionNumber,
  search,
  searchChildren,
  currentSearchId,
  parentSearchId,
  onSelect,
}: Props) => {
  const diffDisplay = useDiffDisplayContext();
  const hasSearchChildren = searchChildren.length > 0;

  const [hover, setHover] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [naturalHeight, setNaturalHeight] = useState<number | null>(null);
  const accordionRef = useRef<HTMLDivElement>(null);

  const majorVersionsParentSearchId =
    hasSearchChildren && expanded
      ? searchChildren[searchChildren.length - 1]?.search_id
      : parentSearchId;

  const isBetweenActiveDiffSearches = checkIfBetweenActiveDiffSearches(
    diffDisplay.targetSearchId,
    diffDisplay.sourceSearchId,
    search.search_id
  );

  // Measure the natural height when the component is not collapsed
  useEffect(() => {
    if (accordionRef.current && !isBetweenActiveDiffSearches) {
      const height = accordionRef.current.scrollHeight;
      setNaturalHeight(height);
    }
  }, [isBetweenActiveDiffSearches, expanded, hasSearchChildren]);

  return (
    <Accordion
      ref={accordionRef}
      key={search.search_id}
      slotProps={{ transition: { unmountOnExit: true, timeout: 200 } }}
      sx={[
        {
          py: 1,
          "&:hover": {
            backgroundColor: "action.hover",
            borderRadius: 1,
          },
          transition: "all 0.3s ease",
          opacity: 1,
          my: 0,
        },
        isBetweenActiveDiffSearches
          ? {
              maxHeight: 0,
              mb: -2,
              pointerEvents: "none",
              overflowY: "hidden",
            }
          : { maxHeight: naturalHeight ?? undefined },
      ]}
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
      onChange={(_, expanded) => {
        setExpanded(expanded);
      }}
    >
      <HistoryItemTitle
        versionNumber={versionNumber}
        search={search}
        hasSearchChildren={hasSearchChildren}
        currentSearchId={currentSearchId}
        parentSearchId={majorVersionsParentSearchId}
        hover={hover}
        onSelect={onSelect}
      />
      {hasSearchChildren ? (
        <AccordionDetails sx={{ pt: 0, pb: 0, pl: 4 }}>
          <Stack>
            {searchChildren.map((search, index) => (
              <HistoryChildItem
                search={search}
                key={search.search_id}
                parentSearchId={
                  index < searchChildren.length - 1
                    ? searchChildren[index + 1]?.search_id
                    : parentSearchId
                }
              />
            ))}
          </Stack>
        </AccordionDetails>
      ) : null}
    </Accordion>
  );
};
